import { AiAssistantRailButton } from "@/components/ai-assistant/AiAssistantRailButton.tsx";
import { useAiAssistantAvailable } from "@/components/ai-assistant/use-ai-assistant-available.hook.ts";
import { SidebarUserMenu } from "@/components/sidebar/components/SidebarUserMenu.tsx";
import { resolveSidebarPanel } from "@/components/sidebar/sidebar-panels.tsx";
import useSidebarMenuTop from "@/components/sidebar/sidebar-service.tsx";
import { usePermissionCheck } from "@/hooks/use-permissions.hook.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useAppRoutes } from "@/routes/use-routes.hook.ts";
import { useEmployeeAppConfigStore } from "@/stores/employee.store.ts";
import { useAppTheme } from "@/themes/app-context.ts";
import { Box, SidebarLayout, SidebarRail, SidebarRailItem } from "@skailer-com/uikit";
import i18n from "i18next";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export default function Sidebar() {
  const sidebarTheme = useAppTheme("sidebar");
  const { data: employee } = useEmployeeMe();
  const navigate = useNavigate();
  const { sidebarIsCollapsed, setSidebarIsCollapsed, sidebarPanelWidth, setSidebarPanelWidth } =
    useEmployeeAppConfigStore();
  const { sidebarIsShowing } = useAppRoutes();
  const { getMenu, menuCounters, canViewOrganizationFolders } = useSidebarMenuTop();

  const menuTop = useMemo(() => {
    return getMenu();
  }, [employee, menuCounters, canViewOrganizationFolders]);

  const { pathname } = useLocation();

  const { hasPermissionsToViewSettings } = usePermissionCheck();
  const isAiAssistantAvailable = useAiAssistantAvailable();

  // Гейт панели: без фильтра по видимым пунктам панель скрытого модуля отрисуется и стрельнёт запросами.
  const visibleItemKeys = useMemo(() => new Set(menuTop.map((item) => item.key)), [menuTop]);

  const panel = resolveSidebarPanel(pathname, visibleItemKeys);

  return (
    <SidebarLayout
      className={"shrink-0 print:hidden"}
      isCollapsed={sidebarIsCollapsed}
      onCollapsedChange={setSidebarIsCollapsed}
      revealOnHover
      resizable
      panelWidth={sidebarPanelWidth}
      onPanelWidthChange={setSidebarPanelWidth}
      rail={
        <SidebarRail
          items={menuTop}
          className={sidebarTheme.menu.base}
          logo={
            <Box
              className={"w-8 h-8 rounded-xs overflow-hidden relative cursor-pointer"}
              onClick={() => {
                navigate("/");
              }}
            >
              <img
                src={employee?.organization?.logo_url || "/favicon-96x96.png"}
                className={"w-8 h-8"}
              />
            </Box>
          }
          footer={
            <>
              {isAiAssistantAvailable && <AiAssistantRailButton />}
              {hasPermissionsToViewSettings ? (
                <SidebarRailItem
                  item={{
                    key: "settings",
                    label: i18n.t("common:settings"),
                    icon: "fa-gear",
                    to: "/settings",
                  }}
                  isActive={pathname.startsWith("/settings")}
                />
              ) : (
                <SidebarRailItem
                  item={{
                    key: "account-settings",
                    label: i18n.t("common:settings"),
                    icon: "fa-gear",
                    to: "/account-settings",
                  }}
                  isActive={false}
                />
              )}

              <SidebarUserMenu />
            </>
          }
        />
      }
      panel={
        sidebarIsShowing && panel ? (
          // attachment fixed — градиент по вьюпорту, как у body; тень на скроллере, иначе bg-light/80 её накроет.
          <div
            className={"w-full h-full relative"}
            style={{
              backgroundImage:
                "linear-gradient(160deg, hsl(var(--var-color-background-gradient-from)) 0%, hsl(var(--var-color-background-gradient-to)) 78.17%)",
              backgroundAttachment: "fixed",
            }}
          >
            <div
              className={
                "h-full shadow-sidebar overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] bg-light/80"
              }
            >
              {panel}
            </div>
          </div>
        ) : undefined
      }
    />
  );
}
